import type {
  KaiPlanArtifact,
  KaiPlanStep,
  KaiPlanStepStatus,
} from "./kaiTypes";
import { formatKaiLiveStatusMessage } from "./kaiStatusHelpers";

const PLAN_STEP_STATUSES = new Set<KaiPlanStepStatus>([
  "pending",
  "running",
  "waiting",
  "ok",
  "error",
  "skipped",
  "cancelled",
]);

function asPlanStepStatus(value: unknown): KaiPlanStepStatus | undefined {
  return typeof value === "string" && PLAN_STEP_STATUSES.has(value as KaiPlanStepStatus)
    ? (value as KaiPlanStepStatus)
    : undefined;
}

export function parsePlanFromMessageMetadata(
  metadata: { plan_execute?: { plan?: unknown } } | null | undefined,
): KaiPlanArtifact | null {
  const plan = metadata?.plan_execute?.plan;
  if (!plan || typeof plan !== "object") return null;
  const raw = plan as { title?: unknown; steps?: unknown };
  if (!Array.isArray(raw.steps) || raw.steps.length === 0) {
    return null;
  }
  const steps: KaiPlanStep[] = [];
  for (const step of raw.steps) {
    if (!step || typeof step !== "object") return null;
    const s = step as { id?: unknown; label?: unknown; status?: unknown };
    if (typeof s.id !== "string" || !s.id) return null;
    if (typeof s.label !== "string" || !s.label) return null;
    steps.push({
      id: s.id,
      label: s.label,
      status: asPlanStepStatus(s.status),
    });
  }
  return {
    title: typeof raw.title === "string" ? raw.title : undefined,
    steps,
  };
}

const SYNTHESIS_STEP_LABEL = /synthesi/i;

export function isSynthesisPlanStep(step: KaiPlanStep): boolean {
  return SYNTHESIS_STEP_LABEL.test(step.label);
}

function stepStatus(step: KaiPlanStep): KaiPlanStepStatus {
  return step.status ?? "pending";
}

/** Plan-and-execute is past tool lookups and composing the final answer. */
export function isPlanFinalizingPhase(
  plan: KaiPlanArtifact | null,
  isGenerating: boolean,
): boolean {
  if (!plan || !isGenerating) return false;

  for (const step of plan.steps) {
    if (isSynthesisPlanStep(step)) continue;
    const status = stepStatus(step);
    if (status === "waiting") return false;
    if (status === "pending" || status === "running") return false;
  }
  return true;
}

export function planFinalizingStatusMessage(
  plan: KaiPlanArtifact | null,
  mcpStatus: string,
): string {
  const formatted = formatKaiLiveStatusMessage(mcpStatus);
  if (formatted) return formatted;

  const synthesis = plan?.steps.find(isSynthesisPlanStep);
  const synthesisStatus = synthesis ? stepStatus(synthesis) : "pending";

  if (synthesisStatus === "running" || synthesisStatus === "pending") {
    return "Putting together your answer…";
  }
  return "Almost ready…";
}
